import type { AnnotatedRelationshipElement } from '../Metamodel/SubmodelElements/AnnotatedRelationshipElement';
import type { Entity } from '../Metamodel/SubmodelElements/Entity';
import type { SubmodelElement } from '../Metamodel/SubmodelElements/SubmodelElement';
import type { SubmodelElementCollection } from '../Metamodel/SubmodelElements/SubmodelElementCollection';
import type { SubmodelElementList } from '../Metamodel/SubmodelElements/SubmodelElementList';
import type { SubmodelTree } from './SubmodelTree';

export class SubmodelElementNode {
  protected _submodel?: SubmodelTree;
  protected _parent?: SubmodelElementNode;

  constructor(
    public element: SubmodelElement,
  ) { }

  get submodel(): SubmodelTree | undefined {
    return this._submodel ?? this.parent?.submodel;
  }

  get parent(): SubmodelElementNode | undefined {
    return this._parent;
  }

  addToSubmodel(submodel: SubmodelTree) {
    if (this._parent) {
      throw new Error('Only root nodes can be added to a submodel.');
    }
    this._submodel = submodel;
  }

  removeFromSubmodel() {
    if (!this._parent) {
      throw new Error('Only root nodes can be removed from a submodel.');
    }
    this._submodel = undefined;
  }

  get isList(): boolean {
    return false;
  }

  get isCollection(): boolean {
    return false;
  }

  get idShortPath(): string {
    let path = this.element.idShort;
    if (this.parent) {
      if (this.parent.isList) {
        const index = this.parent.indexOf(this.element);
        path += `[${index}]`;
      }
      else {
        path = `${this.parent.idShortPath}.${path}`;
      }
    }
    return path;
  }

  indexOf(_child: SubmodelElement): number {
    return -1;
  }

  /**
   * Must be static because child class can't access protected _parent field
   * for other instance
   */
  protected static setParent(parent: SubmodelElementNode, child: SubmodelElementNode) {
    child._parent = parent;
  }

  protected static removeParent(child: SubmodelElementNode) {
    child._parent = undefined;
  }

  static from(element: SubmodelElement): SubmodelElementNode {
    let childNode: SubmodelElementNode;
    switch (element.modelType) {
      case 'AnnotatedRelationshipElement':
      case 'Entity':
      case 'SubmodelElementCollection':
      {
        childNode = new SubmodelElementCollectionNode(element as SubmodelElementCollection);
        const elements: SubmodelElement[] | undefined
            = (element as AnnotatedRelationshipElement).annotations
              ?? (element as Entity).statements
              ?? (element as SubmodelElementCollection).value;
        if (elements) {
          for (const childElement of elements) {
            (childNode as SubmodelElementCollectionNode).set(childElement);
          }
        }
        break;
      }
      case 'SubmodelElementList':
      {
        childNode = new SubmodelElementNodeList(element as SubmodelElementList);
        const sel = element as SubmodelElementList;
        if (sel.value) {
          for (const childElement of sel.value) {
            (childNode as SubmodelElementNodeList).add(childElement);
          }
        }
        break;
      }
      default:
        childNode = new SubmodelElementNode(element);
        break;
    }
    return childNode;
  }

  get children(): SubmodelElementNode[] {
    return [];
  }

  deepSearch(
    predicate?: (element: SubmodelElement) => boolean,
    action?: (element: SubmodelElement) => void,
    maxDepth?: number,
  ): SubmodelElementNode[] {
    const results: SubmodelElementNode[] = [];
    if (!predicate || predicate(this.element)) {
      results.push(this);
      action?.(this.element);
    }
    if (!maxDepth || maxDepth > 0) {
      for (const child of this.children) {
        const childResults = child.deepSearch(predicate, action, maxDepth ? maxDepth - 1 : undefined);
        results.push(...childResults);
      }
    }
    return results;
  }

  breadthFirstSearch(
    predicate?: (element: SubmodelElement) => boolean,
    action?: (element: SubmodelElement) => void,
    maxDepth?: number,
  ): SubmodelElementNode[] {
    const results: SubmodelElementNode[] = [];
    // queue of tuple of [node, depth]
    const queue: [SubmodelElementNode, number][] = [[this, 0]];

    while (queue.length > 0) {
      const [node, depth] = queue.shift()!;
      if (!maxDepth || depth <= maxDepth) {
        if (!predicate || predicate(node.element)) {
          results.push(node);
          action?.(node.element);
        }
        for (const child of node.children) {
          queue.push([child, depth + 1]);
        }
      }
    }
    return results;
  }

  toSubmodelElement(): SubmodelElement {
    return this.element;
  }
}

export class SubmodelElementNodeList extends SubmodelElementNode {
  private _elements: SubmodelElementNode[] = [];
  private _elementNodeMap: Map<SubmodelElement, SubmodelElementNode> = new Map();

  constructor(
    public element: SubmodelElementList,
  ) {
    super(element);
  }

  get isList(): boolean {
    return true;
  }

  indexOf(child: SubmodelElement): number {
    let index = -1;
    const node = this._elementNodeMap.get(child);
    if (node) {
      index = this._elements.indexOf(node);
    }
    return index;
  }

  includes(element: SubmodelElement): boolean {
    return this._elementNodeMap.has(element);
  }

  private addElementToMap(element: SubmodelElement): SubmodelElementNode {
    const childNode: SubmodelElementNode = SubmodelElementNode.from(element);
    this._elementNodeMap.set(element, childNode);
    SubmodelElementNode.setParent(this, childNode);
    return childNode;
  }

  add(element: SubmodelElement) {
    if (this.includes(element)) {
      throw new Error('Element already exists in the list.');
    }
    const childNode = this.addElementToMap(element);
    this._elements.push(childNode);
  }

  replaceAt(index: number, element: SubmodelElement) {
    if (index < 0 || index >= this._elements.length) {
      throw new Error('Index out of bounds.');
    }
    const childNode = this.addElementToMap(element);
    this._elements[index] = childNode;
  }

  removeAt(index: number) {
    if (index < 0 || index >= this._elements.length) {
      throw new Error('Index out of bounds.');
    }
    const elementNode = this._elements[index];
    this._elementNodeMap.delete(elementNode.element);
    this._elements.splice(index, 1);
    SubmodelElementNode.removeParent(elementNode);
  }

  remove(element: SubmodelElement): boolean {
    let removed: boolean = false;
    const index = this.indexOf(element);
    if (index !== -1) {
      this.removeAt(index);
      removed = true;
    }
    return removed;
  }

  clear() {
    this._elements.forEach((elementNode) => {
      SubmodelElementNode.removeParent(elementNode);
    });
    this._elements = [];
    this._elementNodeMap.clear();
  }

  get length(): number {
    return this._elements.length;
  }

  values(): SubmodelElement[] {
    return this._elements.map(node => node.element);
  }

  entries(): [number, SubmodelElement][] {
    return this._elements.map((node, index) => [index, node.element]);
  }

  get(index: number): SubmodelElement {
    if (index < 0 || index >= this._elements.length) {
      throw new Error('Index out of bounds.');
    }
    return this._elements[index].element;
  }

  [Symbol.iterator](): Iterator<SubmodelElement> {
    return this._elements.map(node => node.element)[Symbol.iterator]();
  }

  get children(): SubmodelElementNode[] {
    return Array.from(this._elements);
  }

  toSubmodelElement(): SubmodelElement {
    const sel: SubmodelElementList = this.element;
    sel.value = this._elements.map(node => node.toSubmodelElement());
    return sel;
  }
}

export class SubmodelElementCollectionNode extends SubmodelElementNode {
  private _elementMap: Map<string, SubmodelElementNode> = new Map();

  constructor(
    public element: SubmodelElementCollection | AnnotatedRelationshipElement | Entity,
  ) {
    super(element);
  }

  get isCollection(): boolean {
    return true;
  }

  set(element: SubmodelElement): SubmodelElementCollectionNode {
    if (!element.idShort) {
      throw new Error('Element must have an idShort.');
    }
    if (this._elementMap.has(element.idShort)) {
      this.delete(element.idShort);
    }
    const childNode: SubmodelElementNode = SubmodelElementNode.from(element);
    this._elementMap.set(element.idShort, childNode);
    SubmodelElementNode.setParent(this, childNode);
    return this;
  }

  get(idShort: string): SubmodelElement | undefined {
    return this._elementMap.get(idShort)?.element;
  }

  delete(idShort: string): boolean {
    let deleted = false;
    const node = this._elementMap.get(idShort);
    if (node) {
      this._elementMap.delete(idShort);
      SubmodelElementNode.removeParent(node);
      deleted = true;
    }
    return deleted;
  }

  has(idShort: string): boolean {
    return this._elementMap.has(idShort);
  }

  clear() {
    this._elementMap.forEach((elementNode) => {
      SubmodelElementNode.removeParent(elementNode);
    });
    this._elementMap.clear();
  }

  keys(): string[] {
    return Array.from(this._elementMap.keys());
  }

  values(): SubmodelElement[] {
    return Array.from(this._elementMap.values()).map(node => node.element);
  }

  entries(): [string, SubmodelElement][] {
    return Array.from(this._elementMap.entries()).map(([key, node]) => [key, node.element]);
  }

  forEach(callback: (element: SubmodelElement, idShort: string) => void): void {
    this._elementMap.forEach((node, idShort) => {
      callback(node.element, idShort);
    });
  }

  get size(): number {
    return this._elementMap.size;
  }

  [Symbol.iterator](): Iterator<[string, SubmodelElement]> {
    return this.entries()[Symbol.iterator]();
  }

  get children(): SubmodelElementNode[] {
    return Array.from(this._elementMap.values());
  }

  toSubmodelElement(): SubmodelElement {
    const values = [...this._elementMap.values()].map(node => node.toSubmodelElement());
    if (this.element.modelType === 'AnnotatedRelationshipElement') {
      (this.element as AnnotatedRelationshipElement).annotations = values;
    }
    else if (this.element.modelType === 'Entity') {
      (this.element as Entity).statements = values;
    }
    else {
      (this.element as SubmodelElementCollection).value = values;
    }
    return this.element;
  }
}
